import React, { useState, useEffect } from 'react';

import OrganigramCanvasPlaceholder from '../workforce/OrganigramCanvasPlaceholder';
import UnassignedPositionsPanel from '../workforce/UnassignedPositionsPanel';
import WorkforceTypeFilterPanel from '../workforce/WorkforceTypeFilterPanel';

import SelectInput from 'CommonComponents/SelectInput';

import useOrganigramFilter from 'Hooks/useOrganigramFilter';
import useUnassignedPanel from 'Hooks/useUnassignedPanel';

import OrgChart from 'Scripts/template';

export default function WorkforceOrganigram(props) {
    const [loading, setLoading] = useState(false);
    const [organigram, setOrganigram] = useState(null);
    const [costcenters, setCostcenters] = useState([]);
    const [selectedCcId, setSelectedCcId] = useState('');
    const [collapseLevel, setCollapseLevel] = useState(3);
    const [currentMaxCollapseLevel, setCurrentMaxCollapseLevel] = useState(3);
    const [updatesCompleted, setUpdatesCompleted] = useState(false);

    const preferenceKey = `dept_${props.eventId}`;
    const { filterState, setFilterState, filteredNodes } = useOrganigramFilter({
        initialFilterState: props.filterPreference ? props.filterPreference[preferenceKey] : undefined,
        fullPreferenceValue: props.filterPreference || {},
        preferenceKey,
        nodes: organigram || [],
    });

    useEffect(() => {
        if (props.type === 'department') {
            setCostcenters(props.costcenters);
        }
    }, []);

    useEffect(() => {
        if (updatesCompleted) {
            refresh();
        }
    }, [collapseLevel]);

    useEffect(() => {
        refresh();
    }, []);

    // The unassigned panel is scoped exactly like the chart beside it: picking a
    // cost centre narrows both, so the panel can never offer a position the
    // canvas has nowhere to put.
    const unassignedUrl = selectedCcId ? (props.subUnassignedUrl || '').replace('CC_ID', selectedCcId) : props.unassignedUrl;

    const panel = useUnassignedPanel({
        unassignedUrl,
        treeUpdateUrl: props.treeUpdateUrl,
        onAssigned: () => refresh(),
    });

    const treeParams = () => ({ currentMaxCollapseLevel: currentMaxCollapseLevel, ...panel.treeParams });

    const fetchDepartmentPositions = () => {
        setLoading(true);
        axios
            .get(props.fetchUrl, { params: treeParams() })
            .then((res) => {
                const { data, currentMaxCollapseLevel } = res.data;
                setOrganigram(data);
                setCurrentMaxCollapseLevel(currentMaxCollapseLevel);
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchCostCenterPositions = (ccId) => {
        setLoading(true);
        axios
            .get(props.subFetchUrl.replace('CC_ID', ccId), { params: treeParams() })
            .then((res) => {
                const { data, currentMaxCollapseLevel } = res.data;
                setOrganigram(data);
                setCurrentMaxCollapseLevel(currentMaxCollapseLevel);
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    /** Reload the chart for whichever scope is selected. */
    const refresh = () => (selectedCcId ? fetchCostCenterPositions(selectedCcId) : fetchDepartmentPositions());

    const changeSelect = (value) => {
        setUpdatesCompleted(false);
        setCollapseLevel(3);

        setSelectedCcId(value);
        if (value) {
            return fetchCostCenterPositions(value);
        }

        return fetchDepartmentPositions();
    };

    const changeCollapse = (value) => {
        setUpdatesCompleted(true);
        setCollapseLevel(value);
    };

    // Identity for the PDF export's header, key and breadcrumb. The Blade
    // context names the surface; picking a cost centre narrows it further.
    const exportContext = (() => {
        const context = props.exportContext ?? {};
        const costcenter = costcenters.find((cc) => String(cc.id) === String(selectedCcId));

        return costcenter ? { ...context, scope: [context.scope, costcenter.name].filter(Boolean).join(' · ') } : context;
    })();

    // Only the canvas waits. The dropdowns, the key and the panel are drawn from
    // the first paint, so a department with hundreds of roles does not open on a
    // blank page — and the key is readable while the chart it explains loads.
    const stillLoading = loading || organigram === null;

    return (
        <div className="tw-space-y-4 tw-h-full tw-flex tw-flex-col">
            <div className="tw-flex tw-items-center tw-gap-x-3">
                {props.type === 'department' && (
                    <div className="tw-flex-grow">
                        <SelectInput label={$t('Department')} value={selectedCcId} onChange={(e) => changeSelect(e.target.value)}>
                            <option value="">{$t('Department Organigram')}</option>
                            {costcenters.map((cc) => {
                                return (
                                    <option key={cc.id} value={cc.id}>
                                        {cc.name} Organigram
                                    </option>
                                );
                            })}
                        </SelectInput>
                    </div>
                )}
                <div className="tw-flex-grow">
                    <SelectInput label={$t('Collapse Level')} value={collapseLevel} onChange={(e) => changeCollapse(e.target.value)}>
                        {Array.from({ length: currentMaxCollapseLevel }, (_, index) => (
                            <option key={index + 1} value={index + 1}>
                                Level {index + 1}
                            </option>
                        ))}
                    </SelectInput>
                </div>
            </div>

            {props.eventId && <WorkforceTypeFilterPanel filterState={filterState} onChange={setFilterState} />}

            {/* The panel is positioned rather than laid out beside the chart on
                    purpose: as a flex sibling its hundred-odd rows would set the row's
                    height and stretch the canvas to match. Absolute, it takes the
                    canvas's height and scrolls inside it. */}
            <div className="tw-relative tw-flex tw-h-full tw-min-h-0">
                <div className="tw-h-full tw-w-full tw-min-w-0" style={{ paddingInlineEnd: panel.gutter }}>
                    {stillLoading ? (
                        <OrganigramCanvasPlaceholder />
                    ) : (
                        <OrgChart
                            nodes={filteredNodes}
                            treeUpdateUrl={props.treeUpdateUrl}
                            collapseLevel={collapseLevel}
                            exportContext={exportContext}
                            {...panel.chartProps}
                        />
                    )}
                </div>
                {panel.enabled && <UnassignedPositionsPanel {...panel.panelProps} />}
            </div>
        </div>
    );
}
